import { Eye, PanelLeftOpen, PanelRightClose } from "lucide-react";
import React from "react";
import { useTranslation } from "react-i18next";

export interface WizardPreviewColumnProps {
  /** Header label shown above the content when expanded. */
  title?: string;
  /** Collapsed (rail) state. */
  collapsed: boolean;
  /** Toggle collapsed/expanded. */
  onToggle: () => void;
  /** When true, the column is hidden (display:none) but NOT unmounted. */
  hidden: boolean;
  /** Expanded width in px. */
  width: number;
  /** Consumer-supplied preview content. */
  children: React.ReactNode;
}

/**
 * Infra-owned chrome for the wizard preview column: width, collapse-to-rail,
 * header and toggle. The content comes from the consumer's `previewPanel.render`
 * (passed as children). Internal to WizardInner — not exported from the barrel.
 */
export const WizardPreviewColumn: React.FC<WizardPreviewColumnProps> = ({
  title,
  collapsed,
  onToggle,
  hidden,
  width,
  children,
}) => {
  const { t } = useTranslation();

  const classNames = [
    "tlx-panel-wizard-preview",
    collapsed ? "tlx-panel-wizard-preview--collapsed" : "",
    hidden ? "tlx-panel-wizard-preview--hidden" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <aside
      className={classNames}
      style={{ "--tlx-wizard-preview-w": `${width}px` } as React.CSSProperties}
      aria-hidden={hidden}
      data-test-id="wizard-preview-panel"
    >
      <button
        type="button"
        className="tlx-panel-wizard-preview__toggle"
        onClick={onToggle}
        aria-expanded={!collapsed}
        aria-label={
          collapsed
            ? t("slidePanel.preview.expand")
            : t("slidePanel.preview.collapse")
        }
        title={
          collapsed
            ? t("slidePanel.preview.expand")
            : t("slidePanel.preview.collapse")
        }
        data-test-id="wizard-preview-toggle"
      >
        {collapsed ? (
          <PanelLeftOpen size={16} />
        ) : (
          <PanelRightClose size={16} />
        )}
      </button>

      {collapsed ? (
        <div className="tlx-panel-wizard-preview__rail" aria-hidden>
          <Eye size={18} />
        </div>
      ) : (
        <div className="tlx-panel-wizard-preview__inner">
          {title && (
            <h4 className="tlx-panel-wizard-preview__title">{title}</h4>
          )}
          <div className="tlx-panel-wizard-preview__body">{children}</div>
        </div>
      )}
    </aside>
  );
};

export default WizardPreviewColumn;
